// React
import React from "react";

// MUI
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  styled,
  Typography,
} from "@mui/material";

import { useTranslation } from "react-i18next";
// Third party imports
import { FiLogOut } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
// Routes
import { PROTECTED_ROUTES } from "routes";

// Components
import LanguageSwitcher from "components/LanguageSwitcher";

// Utils
import { BREAKPOINTS, SHELL_NAVBAR_HEIGHT, SHELL_PAD } from "layouts/_shared/shellMetrics";

// Hooks
import useClientPortalCompanies from "hooks/companies/useClientPortalCompanies.jsx";
import useCompanyLite from "hooks/companies/useCompanyLite.jsx";
import useLogout from "hooks/logout/useLogout";

// Contexts
import { FilterProvider } from "context/FilterContext";

import { getClientCompanyId } from "utils/auth.jsx";

// Assets
import logoExtended from "assets/img/meraviglia.svg";

import { colors } from "styles/tokens";

// Tailwind sm (640): MUI's own sm is 600
const UP_SM = `@media (min-width:${BREAKPOINTS.sm}px)`;

/**
 * Lightweight layout for client (CU) users.
 *
 * Deliberately does NOT mount MainSidebar / MainNavbar: they fetch CRM-only
 * endpoints (profile, teams, announcements, notifications) and a 403 from the
 * axios interceptor would log the client out.
 * Only `companies/<id>/lite/` and `companies/client-portal/companies/` are
 * called here (both enabled for client users).
 *
 * A client login can be linked to several companies (e.g. every shop of the
 * same customer): the header then shows a switcher that keeps the current
 * section (board / gallery) while changing company.
 */
// One viewport tall: ContentArea is the only page scroller (same contract as MainLayout)
const PortalContainer = styled(Box)({
  "--shell-navbar-h": `${SHELL_NAVBAR_HEIGHT}px`,
  // Page padding by viewport, the same steps as MainLayout
  "--shell-pad": `${SHELL_PAD.xs}px`,
  [UP_SM]: { "--shell-pad": `${SHELL_PAD.sm}px` },
  [`@media (min-width:${BREAKPOINTS.lg}px)`]: { "--shell-pad": `${SHELL_PAD.lg}px` },
  [`@media (min-width:${BREAKPOINTS.wide}px)`]: { "--shell-pad": `${SHELL_PAD.wide}px` },
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100dvh",
  overflow: "hidden",
  backgroundColor: colors.white,
});
const PortalHeader = styled(Box)({
  position: "relative",
  flexShrink: 0,
  zIndex: "var(--z-appbar)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  boxSizing: "border-box",
  height: "var(--shell-navbar-h)",
  padding: "0 12px",
  backgroundColor: colors.white,
  borderBottom: `1px solid ${colors.gray200}`,
  [UP_SM]: { gap: 16, padding: "0 16px" },
});
const HeaderSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  minWidth: 0,
  [UP_SM]: { gap: 12 },
});
const ContentArea = styled(Box)({
  flex: "1 1 0%",
  minHeight: 0,
  padding: "var(--shell-pad)",
  overflow: "auto",
  overscrollBehavior: "contain",
  scrollbarGutter: "stable",
  // The portal has no AI assistant: --fab-safe is 0 here, kept so the contract is the same as
  // MainLayout (document pages reserve it, fill views end --shell-pad above the bottom edge)
  paddingBottom: "calc(var(--shell-pad) + var(--fab-safe, 0px))",
  "&:has([data-shell-fill])": { scrollbarGutter: "auto", paddingBottom: "var(--shell-pad)" },
  display: "flex",
  flexDirection: "column",
});

// `/<company_id>/board` or `/<company_id>/gallery`
const PORTAL_PATH_REGEX = /^\/([^/]+)\/(board|gallery)\/?$/;

const ClientPortalLayout = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleLogout } = useLogout();

  const match = location.pathname.match(PORTAL_PATH_REGEX);
  const companyId = match?.[1] || getClientCompanyId();
  const section = match?.[2] || "board";

  const { companies } = useClientPortalCompanies();
  const { liteSelectedCompany, isLoading } = useCompanyLite(companyId, !!companyId);

  const companyName =
    (liteSelectedCompany?.id === companyId ? liteSelectedCompany?.name : null) ||
    companies.find((company) => company.id === companyId)?.name ||
    null;
  const hasSwitcher = companies.length > 1;

  // The linked companies may have changed since login (access revoked by an operator):
  // leave a company that is no longer reachable.
  React.useEffect(() => {
    if (
      companies.length > 0 &&
      companyId &&
      !companies.some((company) => company.id === companyId)
    ) {
      navigate(PROTECTED_ROUTES.CLIENT_BOARD(companies[0].id), { replace: true });
    }
  }, [companies, companyId, navigate]);

  const handleCompanyChange = (event) => {
    const nextCompanyId = event.target.value;
    if (!nextCompanyId || nextCompanyId === companyId) return;
    localStorage.setItem("company_id", JSON.stringify(nextCompanyId));
    const route = section === "gallery" ? PROTECTED_ROUTES.GALLERY : PROTECTED_ROUTES.CLIENT_BOARD;
    navigate(route(nextCompanyId));
  };

  return (
    <FilterProvider navigate={navigate}>
      <PortalContainer>
        <PortalHeader component="header">
          <HeaderSection sx={{ flex: "1 1 0" }}>
            <Box
              component="img"
              src={logoExtended}
              alt={t("navigation:logoAlt", "Logo")}
              sx={{ height: 24, flexShrink: 0, objectFit: "contain", [UP_SM]: { height: 28 } }}
            />
            <Box
              sx={{
                width: "1px",
                height: 28,
                flexShrink: 0,
                bgcolor: colors.gray200,
                display: { xs: "none", sm: "block" },
              }}
            />
            {hasSwitcher ? (
              <Select
                size="small"
                value={companies.some((company) => company.id === companyId) ? companyId : ""}
                onChange={handleCompanyChange}
                displayEmpty
                inputProps={{ "aria-label": t("client:portal.switchCompany", "Cambia sede") }}
                renderValue={(value) =>
                  companies.find((company) => company.id === value)?.name ||
                  companyName ||
                  t("client:portal.title", "Area cliente")
                }
                sx={{
                  flex: "1 1 0",
                  minWidth: 0,
                  maxWidth: 320,
                  fontWeight: 600,
                  [UP_SM]: { minWidth: 180 },
                }}
                MenuProps={{ slotProps: { paper: { sx: { maxHeight: 360 } } } }}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            ) : isLoading && !companyName ? (
              <Skeleton variant="text" width={160} height={24} animation="wave" />
            ) : (
              <Typography
                component="h1"
                variant="subtitle1"
                noWrap
                sx={{ fontWeight: 600, color: colors.gray700, minWidth: 0 }}
                title={companyName || ""}
              >
                {companyName || t("client:portal.title", "Area cliente")}
              </Typography>
            )}
          </HeaderSection>

          <HeaderSection sx={{ flexShrink: 0 }}>
            {hasSwitcher && (
              <Typography
                variant="body2"
                sx={{ color: colors.gray500, display: { xs: "none", lg: "block" } }}
              >
                {t("client:portal.companiesCount", {
                  count: companies.length,
                  defaultValue: "{{count}} sedi",
                })}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{ color: colors.gray500, display: { xs: "none", md: "block" } }}
            >
              {t("client:portal.readOnlyBadge", "Sola lettura")}
            </Typography>
            <LanguageSwitcher />
            {/* Below 640 the logout is icon only */}
            <IconButton
              onClick={handleLogout}
              aria-label={t("common:logout", "Esci")}
              sx={{ display: "inline-flex", [UP_SM]: { display: "none" } }}
            >
              <FiLogOut />
            </IconButton>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              startIcon={<FiLogOut />}
              onClick={handleLogout}
              sx={{ display: "none", [UP_SM]: { display: "inline-flex" } }}
            >
              {t("common:logout", "Esci")}
            </Button>
          </HeaderSection>
        </PortalHeader>
        <ContentArea component="main">{children}</ContentArea>
      </PortalContainer>
    </FilterProvider>
  );
};

export default ClientPortalLayout;
